// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Modal from '@bit/atd.web.modal'
import Button from '@bit/atd.web.button'
// @module components
import PricingProfessional from 'modules/onBoarding/components/PricingProfessional'
import PricingProfessionalPlus from 'modules/onBoarding/components/PricingProfessionalPlus'
import PricingEnterprise from 'modules/onBoarding/components/PricingEnterprise'
import Navigation from 'modules/onBoarding/components/_common/Navigation'

import { ExternalRouteNames } from 'constants/routeNames'
import queryString from 'query-string'
import { Slide } from '@material-ui/core'
import {
  Direction,
  NavigationContext,
} from '../../../common/context/navigation'
import useNavigation, { SuccessAction } from '../../hooks/useNavigation'
import strings from 'lib/constants/strings'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& form': {
      width: '100%',
    },
  },
  h2: {
    color: '#2E2D31',
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    marginBottom: '16px',
  },
  text: {
    fontSize: '16px',
    lineHeight: '22px',
  },
  cardContainer: {
    '& > div:first-child': {
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
  },
  ActionButtons: {
    display: 'flex',
    '& button:last-child': {
      marginLeft: '12px',
    },
  },
}))

/**
 *
 * @constructor
 */
const Membership = (): ReactElement => {
  /********************************************
   * STATE
   **********************************************/
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState({ tier: '', type: '' })
  // Stores File object from profile picture
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  /********************************************
   * HOOKS
   **********************************************/
  const classes = useStyles()
  const history = useHistory()
  const navigation = useContext(NavigationContext)
  useEffect(() => {
    setSlidingIn(true)
  }, [])

  const { successHandler } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty: false,
  })

  const handlePrevClick = () => {
    successHandler(SuccessAction.PREV)
  }

  const handleNextClick = () => {
    successHandler(SuccessAction.EXIT)
  }

  /********************************************
   * FUNCTIONS
   **********************************************/

  /**
   * @param options
   */
  const cartURLBuilder = (options: {
    productId: number
    rateCode?: string
  }) => {
    const baseURL = ExternalRouteNames.CHECKOUT_HOME
    const params = queryString.stringify({
      ...options,
      x: 1,
    })
    return `${baseURL}/ShoppingCart/AddToCart?${params}`
  }

  /**
   *
   * @param tier
   * @param type
   */
  const handleMemberOnClick = (tier?: string, type?: string) => {
    if (tier && type) {
      switch (tier) {
        case 'professional':
          switch (type) {
            case 'join-now':
              window.location.assign(cartURLBuilder({ productId: 25367 }))
              break
            case 'learn-more':
              window.location.assign(ExternalRouteNames.ATD_JOIN)
              break
            case 'select-student':
              setShowModal(true)
              setModalData({ tier, type })
              break
            case 'select-senior':
              setShowModal(true)
              setModalData({ tier, type })
              break
            case 'select-professional':
              setShowModal(true)
              setModalData({ tier, type })
              break
          }
          break
        case 'professional-plus':
          switch (type) {
            case 'join-now':
              // cartURLBuilder({ productId: 25360, rateCode: 'STUDENT' })
              window.location.assign(cartURLBuilder({ productId: 25360 }))
              break
            case 'learn-more':
              window.location.assign(ExternalRouteNames.ATD_JOIN)
              break
            case 'select-student':
              setShowModal(true)
              setModalData({ tier, type })
              break
            case 'select-senior':
              setShowModal(true)
              setModalData({ tier, type })
              break
            case 'select-professional':
              setShowModal(true)
              setModalData({ tier, type })
              break
          }
          break
        case 'enterprise':
          switch (type) {
            case 'learn-more':
              window.location.assign(ExternalRouteNames.ATD_ENTERPRISE_HOME)
              break
          }
          break
      }
    }
  }

  /**
   *
   */
  const handleModalOnClick = (tier: string, type: string) => {
    switch (tier) {
      case 'professional':
        switch (type) {
          case 'select-student':
            window.location.assign(
              cartURLBuilder({ productId: 25367, rateCode: 'STUDENT' }),
            )
            break
          case 'select-senior':
            window.location.assign(
              cartURLBuilder({ productId: 25367, rateCode: 'SENIOR' }),
            )
            break
          case 'select-professional':
            window.location.assign(
              cartURLBuilder({ productId: 25367, rateCode: 'YOUNG' }),
            )
            break
        }
        break
      case 'professional-plus':
        switch (type) {
          case 'select-student':
            window.location.assign(
              cartURLBuilder({ productId: 25360, rateCode: 'STUDENT' }),
            )
            break
          case 'select-senior':
            window.location.assign(
              cartURLBuilder({ productId: 25360, rateCode: 'SENIOR' }),
            )
            break
          case 'select-professional':
            window.location.assign(
              cartURLBuilder({ productId: 25360, rateCode: 'YOUNG' }),
            )
            break
        }
        break
    }
  }

  /**********************
   * RENDER
   ***********************/

  const modal = (
    <Modal
      forceAction
      footerNoSeparator
      headerNoSeparator
      open={showModal}
      size="small"
      title={`${
        (modalData.type === 'select-student' && 'Student') ||
        (modalData.type === 'select-senior' && 'Senior') ||
        (modalData.type === 'select-professional' && 'Young Professional') ||
        ''
      } Membership`}
      actions={
        <div className={classes.ActionButtons}>
          <Button
            variant="tertiary"
            size="medium"
            onClick={() => {
              setShowModal(false)
            }}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={() => {
              handleModalOnClick(modalData.tier, modalData.type)
              setShowModal(false)
            }}
          >
            I Agree
          </Button>
        </div>
      }
    >
      <p>
        I qualify to meet the{' '}
        {(modalData.type === 'select-student' && 'student') ||
          (modalData.type === 'select-senior' && 'senior') ||
          (modalData.type === 'select-professional' && 'young professional') ||
          ''}{' '}
        {(modalData.tier === 'professional' && 'professional') ||
          (modalData.tier === 'professional-plus' && 'professional plus') ||
          ''}{' '}
        membership eligibility:
      </p>
      {(modalData.type === 'select-student' && (
        <ul>
          <li>
            Enrolled in a minimum of 12 credit hours per year (or the equivalent
            of at least half-time) at an accredited university or college
          </li>
          <li>OR an active ATD chapter student member</li>
        </ul>
      )) ||
        (modalData.type === 'select-senior' && (
          <ul>
            <li>At least 65 years of age</li>
          </ul>
        )) ||
        (modalData.type === 'select-professional' && (
          <ul>
            <li>35 years of age or younger</li>
          </ul>
        )) || <></>}
    </Modal>
  )

  return (
    <>
      <Spacer size={32} fullWidth />

      <Slide
        in={slidingIn}
        timeout={500}
        direction={navigation.direction}
        unmountOnExit
        onExited={() => {
          navigation.setDirection(direction)
          history.push(destination)
        }}
      >
        <div>
          <Container>
            <Grid className={classes.container} container>
              <Grid item xs={12}>
                <Typography className={classes.h2} component="h2">
                  Put the Power of Membership to Work for You
                </Typography>
                <Typography className={classes.text} component="p">
                  {strings.ONBOARDING.MEMBERSHIP.SUBTITLE}
                </Typography>
              </Grid>
            </Grid>
          </Container>

          <Spacer size={32} fullWidth />

          {/* Pricing Plans */}
          <Container>
            <Grid className={classes.container} container>
              <Grid item xs={12}>
                <Grid className={classes.cardContainer} container spacing={4}>
                  <Grid item xs={12} sm={6}>
                    <PricingProfessional
                      handleMemberOnClick={handleMemberOnClick}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PricingProfessionalPlus
                      handleMemberOnClick={handleMemberOnClick}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PricingEnterprise
                      handleMemberOnClick={handleMemberOnClick}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </div>
      </Slide>

      <Navigation
        handlePrevOnClick={() => handlePrevClick()}
        handleNextOnClick={() => handleNextClick()}
        prevPage="Follow Topics"
        nextPage="No thanks, finish now"
      />

      {/* Modal */}
      {modal}
    </>
  )
}

export default Membership
